import React, { useState, useRef } from 'react';
import { TextField, Select, MenuItem, FormControl, Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Grid } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { updateCardCustomField } from '../../../../../sevices/apiCards';
import { useParams, useSearchParams } from 'react-router-dom';
import useToastPromise from '../../../../../hooks/useToast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CustomDate from './components/CustomDate';
import useBoardIdByKey from '../../../../../hooks/useBoardIdByKey';

const CustomFields = ({ field, boardPermission }) => {
    const queryClient = useQueryClient();
    const { type: field_type, display_name, settings, value } = field || {};
    const { showToastPromise } = useToastPromise();
    const { cardId } = useParams();
    const [textValue, setTextValue] = useState(value || '');
    const [numberValue, setNumberValue] = useState(value || '');
    const [lastApiValue, setLastApiValue] = useState(value || '');
    const enterPressedRef = useRef(false);
    const numberEnterPressedRef = useRef(false);
    const { id } = useBoardIdByKey();
    const [searchParams] = useSearchParams();


    const { mutate: updateCardmutate, isPending } = useMutation({
        mutationFn: async (data) => {
            const payload = { column_key: data.column_key, value: data.value }
            await showToastPromise(() => updateCardCustomField(cardId?.split('-')?.[1], payload), payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["card", Number(cardId.split('-')[1])],
            });
            queryClient.invalidateQueries({
                queryKey: ["history", Number(cardId.split('-')[1])],
            });

            queryClient.invalidateQueries({
                queryKey: ["feeds", id, searchParams.get("milestone")]
            });
            queryClient.invalidateQueries({
                queryKey: [`cards`, Number(id)],
            });
            queryClient.invalidateQueries({
                queryKey: [`mineCards`],
              });queryClient.invalidateQueries({
                queryKey: [`userCards`],
              });queryClient.invalidateQueries({
                queryKey: [`assignedCards`],
              });
              queryClient.invalidateQueries({
                queryKey: [`watching`],
              });
        },
    });

    const handleFieldChange = (newValue) => {
        updateCardmutate({
            column_key: field?.column_key,
            value: newValue,
        });
    };

    const handleTextChange = (e) => {
        setTextValue(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && textValue !== lastApiValue) {
            enterPressedRef.current = true;
            handleFieldChange(textValue);
            setLastApiValue(textValue);
            e.target.blur();
        }
    };

    const handleBlur = () => {
        if (!enterPressedRef.current && textValue !== lastApiValue) {
            handleFieldChange(textValue);
            setLastApiValue(textValue);
        }
        enterPressedRef.current = false;
    };

    const handleSelectChange = (event) => {
        handleFieldChange(event.target.value);
    };

    const handleRadioChange = (event) => {
        if (event.target.value === value) {
            handleFieldChange('');
        } else {
            handleFieldChange(event.target.value);
        }
    };

    const handleCheckboxChange = (event) => {
        const parsedValue = value ? JSON.parse(value) : [];
        const checkedValues = event.target.checked
            ? [...parsedValue, event.target.name]
            : parsedValue.filter(v => v !== event.target.name);

        handleFieldChange(checkedValues.length === 0 ? "" : JSON.stringify(checkedValues));
    };

    const handleDateChange = (newDate) => {
        if (newDate !== lastApiValue) {
            handleFieldChange(newDate);
            setLastApiValue(newDate);
        }
    };

    const handleNumberChange = (event) => {
        setNumberValue(event.target.value);
    };

    const handleNumberKeyDown = (e) => {
        if (e.key === "Enter" && Number(numberValue) !== Number(lastApiValue)) {
            numberEnterPressedRef.current = true;
            handleFieldChange(Number(numberValue));
            setLastApiValue(numberValue);
            e.target.blur();
        }
    };

    const handleNumberBlur = () => {
        if (!numberEnterPressedRef.current && Number(numberValue) !== Number(lastApiValue)) {
            handleFieldChange(Number(numberValue));
            setLastApiValue(numberValue);
        }
        numberEnterPressedRef.current = false;
    };

    const renderField = () => {
        switch (field_type) {
            case 'text':
                return (
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={textValue}
                        onChange={handleTextChange}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        className="custom-field-text"
                        inputProps={{ maxLength: 25 }}
                        disabled={!boardPermission?.can_edit_task}
                    />
                );

            case 'select':
                return (
                    <FormControl fullWidth size="small">
                        <Select
                            value={value || ''}
                            onChange={handleSelectChange}
                            disabled={!boardPermission?.can_edit_task}
                            sx={{
                                width: "100%",
                                height: "32px",
                                marginRight: "0px",
                            }}>
                            <MenuItem key={12345678987654} value={''}>
                                None
                            </MenuItem>
                            {settings?.map((option, index) => (
                                <MenuItem key={index} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case "multi_choice":
                return (
                    <FormControl component="fieldset" disabled={!boardPermission?.can_edit_task}>
                        <RadioGroup
                            className="custom-field-radio"
                            value={value || ''}
                            sx={{ pointerEvents: isPending ? 'none' : 'auto' }}>
                            {settings?.map((option, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={option}
                                    control={
                                        <Radio
                                            checked={value === option}
                                            onClick={() => {
                                                if (value === option) {
                                                    handleFieldChange('');
                                                } else {
                                                    handleFieldChange(option);
                                                }
                                            }}
                                        />
                                    }
                                    label={option}
                                    sx={{ pointerEvents: isPending ? 'none' : 'auto' }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                );

            case "checkbox":
                return (
                    <FormControl component="fieldset" disabled={!boardPermission?.can_edit_task}>
                        <FormGroup className="custom-field-checkbox"
                            sx={{ pointerEvents: isPending ? 'none' : 'auto' }}>
                            {settings?.map((option, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={option}
                                    

                                    control={
                                        <Checkbox
                                           
                                            checked={value ? (Array.isArray(JSON.parse(value)) ? JSON.parse(value).includes(option) : [].includes(option)) : false}
                                            onChange={handleCheckboxChange}
                                            name={option}
                                            value={option}
                                            sx={{ pointerEvents: isPending ? 'none' : 'auto' }}
                                        />
                                    }
                                    label={option}
                                />
                            ))}
                        </FormGroup>
                    </FormControl>
                );

            case 'date':
                return (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <CustomDate value={value == 0 ? null : value} updateCardmutate={handleDateChange} disabled={!boardPermission?.can_edit_task} />
                    </LocalizationProvider>
                );

            case 'number':
                return (
                    <TextField
                        fullWidth
                        type="number"
                        variant="outlined"
                        size="small"
                        value={numberValue}
                        onChange={handleNumberChange}
                        onBlur={handleNumberBlur}
                        onKeyDown={handleNumberKeyDown}
                        className="custom-field-text"
                        disabled={!boardPermission?.can_edit_task}
                    />
                );

            default:
                return <div>Unsupported field type: {field_type}</div>;
        }
    };

    return (
        <div className="custom-field-container">
            <div className="box">
                <Grid item className="allocated-hours">
                    <p>{display_name}</p>
                    <div
                        className="taskdetail-sidebar-right-col">
                        {renderField()}
                    </div>
                </Grid>
            </div>
        </div>
    );
};

export default CustomFields;
